import React, { useEffect, useState } from 'react';
import { CommandDialog, CommandInput, CommandList, CommandEmpty, CommandGroup, CommandItem } from '@/components/ui/command';
import { useNavigate } from 'react-router-dom';
import { Users, Building2, CheckSquare, MessageSquare, LayoutDashboard, KanbanSquare } from 'lucide-react';
import { api } from '@/lib/api';
import { LAYOUT } from '@/constants/testIds';

export default function CommandPalette({ open, setOpen, onNavigate }) {
  const [q, setQ] = useState('');
  const [results, setResults] = useState({ people: [], companies: [], tasks: [], notes: [] });
  const navigate = useNavigate();

  useEffect(() => {
    if (!open) { setQ(''); setResults({ people: [], companies: [], tasks: [], notes: [] }); }
  }, [open]);

  useEffect(() => {
    const t = setTimeout(async () => {
      if (q.trim().length < 2) { setResults({ people: [], companies: [], tasks: [], notes: [] }); return; }
      try {
        const { data } = await api.get(`/search`, { params: { q } });
        setResults(data);
      } catch (e) { /* ignore */ }
    }, 150);
    return () => clearTimeout(t);
  }, [q]);

  const go = (path) => { setOpen(false); (onNavigate || navigate)(path); };

  return (
    <CommandDialog open={open} onOpenChange={setOpen}>
      <CommandInput data-testid={LAYOUT.globalSearchInput} placeholder="Search anything — contacts, prospects, tasks, notes…" value={q} onValueChange={setQ} />
      <CommandList>
        <CommandEmpty>{q.length < 2 ? 'Type at least 2 characters…' : 'No results.'}</CommandEmpty>

        {q.length < 2 && (
          <CommandGroup heading="Jump to">
            <CommandItem onSelect={() => go('/')}><LayoutDashboard className="w-4 h-4 mr-2" />Dashboard</CommandItem>
            <CommandItem onSelect={() => go('/people')}><Users className="w-4 h-4 mr-2" />Individual Prospects</CommandItem>
            <CommandItem onSelect={() => go('/companies')}><Building2 className="w-4 h-4 mr-2" />Business Prospects</CommandItem>
            <CommandItem onSelect={() => go('/pipeline')}><KanbanSquare className="w-4 h-4 mr-2" />Pipeline</CommandItem>
            <CommandItem onSelect={() => go('/tasks')}><CheckSquare className="w-4 h-4 mr-2" />Tasks</CommandItem>
          </CommandGroup>
        )}

        {results.people.length > 0 && (
          <CommandGroup heading="Individual Prospects">
            {results.people.map((p) => (
              <CommandItem key={p.id} onSelect={() => go(`/people/${p.id}`)}>
                <Users className="w-4 h-4 mr-2 text-muted-foreground" />
                <div className="flex flex-col"><span>{p.name}</span><span className="text-xs text-muted-foreground">{p.company_name || p.job_title || ''}</span></div>
              </CommandItem>
            ))}
          </CommandGroup>
        )}
        {results.companies.length > 0 && (
          <CommandGroup heading="Business Prospects">
            {results.companies.map((c) => (
              <CommandItem key={c.id} onSelect={() => go(`/companies/${c.id}`)}>
                <Building2 className="w-4 h-4 mr-2 text-muted-foreground" />{c.name}
              </CommandItem>
            ))}
          </CommandGroup>
        )}
        {results.tasks.length > 0 && (
          <CommandGroup heading="Tasks">
            {results.tasks.map((t) => (
              <CommandItem key={t.id} onSelect={() => go('/tasks')}>
                <CheckSquare className="w-4 h-4 mr-2 text-muted-foreground" />{t.title}
              </CommandItem>
            ))}
          </CommandGroup>
        )}
        {results.notes.length > 0 && (
          <CommandGroup heading="Notes">
            {results.notes.map((n) => (
              <CommandItem key={n.id} onSelect={() => n.person_id ? go(`/people/${n.person_id}`) : (n.company_id ? go(`/companies/${n.company_id}`) : null)}>
                <MessageSquare className="w-4 h-4 mr-2 text-muted-foreground" />
                <span className="line-clamp-1">{n.content}</span>
              </CommandItem>
            ))}
          </CommandGroup>
        )}
      </CommandList>
    </CommandDialog>
  );
}
